import React, { useState, useEffect } from 'react';

const API_URL = 'https://team-profiles-api.mikaelfuhr.workers.dev';
const PROF_CODE = 'prof2026';

const ROLES = [
  { id: 'organiser',  label: 'The Organiser',  desc: 'Keeps things on track, makes the plan' },
  { id: 'ideator',    label: 'The Ideator',    desc: 'Generates ideas, connects dots' },
  { id: 'doer',       label: 'The Doer',       desc: 'Gets things done, executes' },
  { id: 'connector',  label: 'The Connector',  desc: 'Builds relationships, keeps team together' },
  { id: 'challenger', label: 'The Challenger', desc: 'Questions assumptions, pushes back' },
];

const SLIDERS = [
  { id: 'energy',    label: 'Energy source',       left: 'Solitary',    right: 'Collaborative' },
  { id: 'pace',      label: 'Work pace',            left: 'Slow & deep', right: 'Fast & broad' },
  { id: 'structure', label: 'Structure preference', left: 'Flexible',    right: 'Structured' },
  { id: 'conflict',  label: 'Conflict style',       left: 'Avoiding',    right: 'Confronting' },
];

const QUESTIONS = [
  { id: 'q1', label: "What's something you're weirdly competitive about that has absolutely nothing to do with school?" },
  { id: 'q2', label: "What's the worst group project you've ever been part of – and what made it so bad?" },
  { id: 'q3', label: "What's a habit or pattern of yours that has caused problems in a team before – and have you actually fixed it, or do you just know about it?" },
  { id: 'q4', label: "What does a good day of collaboration look like for you – in school or otherwise – and what completely derails you?" },
];

const EMOJIS = ['🦊','🐼','🦁','🐙','🦜','🦒','🐺','🦋','🐬','🦅','🐘','🐢','🦔','🦦','🐿️','🦩','🐲','🦭','🦬','🦘','🌵','🌊','⚡','🎯','🔥','🌙','🎸','🚀','🎨','🧩','🏔️','🌺'];

const T = {
  bg: '#F5F0EB', card: '#FFFFFF', border: '#E2DCD5',
  text: '#2C2825', text2: 'rgb(122, 116, 112)',
  accent: '#C75C3A', dark: '#2C2825',
  font: '"IBM Plex Sans", -apple-system, sans-serif',
  mono: '"IBM Plex Mono", monospace',
};

const globalCss = `
  * { box-sizing: border-box; margin: 0; padding: 0; }
  body { font-family: ${T.font}; background: ${T.bg}; color: ${T.text}; -webkit-text-size-adjust: 100%; }
  input[type=range] { -webkit-appearance: none; appearance: none; width: 100%; height: 8px; border-radius: 4px; background: #E2DCD5; outline: none; margin: 4px 0; }
  input[type=range]::-webkit-slider-thumb { -webkit-appearance: none; appearance: none; width: 28px; height: 28px; border-radius: 50%; background: ${T.accent}; cursor: pointer; border: 3px solid #fff; box-shadow: 0 1px 4px rgba(0,0,0,0.2); }
  input[type=range]::-moz-range-thumb { width: 28px; height: 28px; border-radius: 50%; background: ${T.accent}; cursor: pointer; border: 3px solid #fff; }
  @keyframes spin { to { transform: rotate(360deg); } }
`;

function Wrap({ children }) {
  return (
    <div style={{ fontFamily: T.font, color: T.text, background: T.bg, minHeight: '100vh' }}>
      <style>{globalCss}</style>
      <div style={{ maxWidth: 480, margin: '0 auto', padding: '48px 20px' }}>{children}</div>
    </div>
  );
}

function Label({ children }) {
  return <div style={{ fontSize: 11, fontFamily: T.mono, textTransform: 'uppercase', letterSpacing: 2, color: T.accent, marginBottom: 12 }}>{children}</div>;
}
function StepLabel({ step }) {
  return <div style={{ fontSize: 11, fontFamily: T.mono, textTransform: 'uppercase', letterSpacing: 2, color: T.text2, marginBottom: 6 }}>Step {step} of 4</div>;
}
function H1({ children }) {
  return <h1 style={{ fontSize: 26, fontWeight: 600, lineHeight: 1.3, margin: '0 0 12px' }}>{children}</h1>;
}
function H2({ children }) {
  return <h2 style={{ fontSize: 22, fontWeight: 600, lineHeight: 1.3, margin: '0 0 8px' }}>{children}</h2>;
}
function Sub({ children }) {
  return <p style={{ fontSize: 15, color: T.text2, lineHeight: 1.6, margin: '0 0 24px' }}>{children}</p>;
}
function PrimaryBtn({ onClick, disabled, loading, children }) {
  return (
    <button onClick={onClick} disabled={disabled || loading}
      style={{ width: '100%', padding: '18px 24px', fontSize: 16, fontWeight: 600, fontFamily: T.font, background: disabled ? '#ccc' : T.accent, color: '#fff', border: 'none', borderRadius: 12, cursor: disabled || loading ? 'not-allowed' : 'pointer', marginBottom: 16, minHeight: 56, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8, opacity: disabled ? 0.6 : 1 }}>
      {loading && <span style={{ width: 18, height: 18, border: '2px solid rgba(255,255,255,0.3)', borderTopColor: '#fff', borderRadius: '50%', animation: 'spin 0.7s linear infinite', display: 'inline-block' }} />}
      {children}
    </button>
  );
}
function SecondaryBtn({ onClick, children }) {
  return (
    <button onClick={onClick}
      style={{ width: '100%', padding: '14px 24px', fontSize: 15, fontWeight: 500, fontFamily: T.font, background: 'transparent', color: T.text2, border: `1.5px solid ${T.border}`, borderRadius: 12, cursor: 'pointer', minHeight: 50 }}>
      {children}
    </button>
  );
}
function Card({ children }) {
  return <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 14, padding: '20px', marginBottom: 16 }}>{children}</div>;
}
function SliderRow({ slider, value, onChange }) {
  return (
    <div style={{ marginBottom: 20 }}>
      <div style={{ fontSize: 13, fontWeight: 500, color: T.text, marginBottom: 10 }}>{slider.label}</div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: 12, color: T.text2, width: 80, textAlign: 'right', flexShrink: 0, lineHeight: 1.3 }}>{slider.left}</span>
        <input type="range" min={1} max={10} value={value} onChange={e => onChange(+e.target.value)} style={{ flex: 1 }} />
        <span style={{ fontSize: 12, color: T.text2, width: 80, flexShrink: 0, lineHeight: 1.3 }}>{slider.right}</span>
      </div>
    </div>
  );
}

function generateCode() {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789';
  let code = '';
  for (let i = 0; i < 6; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
}

export default function App() {
  const [screen, setScreen]         = useState('home');
  const [codeInput, setCodeInput]   = useState('');
  const [codeError, setCodeError]   = useState('');
  const [profInput, setProfInput]   = useState('');
  const [profError, setProfError]   = useState('');
  const [profiles, setProfiles]     = useState([]);
  const [expanded, setExpanded]     = useState(null);
  const [deleting, setDeleting]     = useState(null);
  const [saving, setSaving]         = useState(false);
  const [saveError, setSaveError]   = useState('');
  const [myCode, setMyCode]         = useState('');
  const [loadingCode, setLoadingCode] = useState(false);

  const [name,    setName]    = useState('');
  const [emoji,   setEmoji]   = useState('🦊');
  const [role,    setRole]    = useState('');
  const [sliders, setSliders] = useState({ energy: 5, pace: 5, structure: 5, conflict: 5 });
  const [answers, setAnswers] = useState({ q1: '', q2: '', q3: '', q4: '' });

  const pct = v => Math.round(((v - 1) / 9) * 100);

  async function lookupCode() {
    const code = codeInput.trim().toUpperCase();
    if (!code) return;
    setLoadingCode(true); setCodeError('');
    try {
      const r = await fetch(`${API_URL}/api/profiles/${code}`);
      if (!r.ok) { setCodeError('Profile not found. Check the code and try again.'); setLoadingCode(false); return; }
      const p = await r.json();
      setName(p.name || ''); setEmoji(p.emoji || '🦊'); setRole(p.role || '');
      setSliders(p.sliders || { energy: 5, pace: 5, structure: 5, conflict: 5 });
      setAnswers(p.answers || { q1: '', q2: '', q3: '', q4: '' });
      setMyCode(code); setScreen('step1');
    } catch(e) { setCodeError('Could not connect. Try again.'); }
    setLoadingCode(false);
  }

  async function save() {
    setSaving(true); setSaveError('');
    try {
      const code = myCode || generateCode();
      const profile = { name: name.trim(), emoji, role, sliders, answers, code, savedAt: Date.now() };
      const r = await fetch(`${API_URL}/api/profiles`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(profile)
      });
      if (!r.ok) throw new Error('Save failed');
      setMyCode(code); setScreen('done');
    } catch(e) { setSaveError('Could not save – check your connection and try again.'); }
    setSaving(false);
  }

  async function loadProfiles() {
    try {
      const r = await fetch(`${API_URL}/api/profiles`);
      const j = await r.json();
      setProfiles(j.sort((a, b) => (b.savedAt || 0) - (a.savedAt || 0)));
    } catch(e) {}
  }

  async function deleteProfile(name) {
    setDeleting(name);
    try {
      await fetch(`${API_URL}/api/profiles/${encodeURIComponent(name)}`, { method: 'DELETE' });
      await loadProfiles();
    } catch(e) {}
    setDeleting(null);
  }

  useEffect(() => { if (screen === 'prof') loadProfiles(); }, [screen]);

  function resetAndHome() {
    setName(''); setEmoji('🦊'); setRole('');
    setSliders({ energy: 5, pace: 5, structure: 5, conflict: 5 });
    setAnswers({ q1: '', q2: '', q3: '', q4: '' });
    setMyCode(''); setCodeInput(''); setCodeError('');
    setScreen('home');
  }

  // ── HOME
  if (screen === 'home') return (
    <Wrap>
      <div style={{ marginBottom: 40 }}>
        <Label>Team Profile Builder</Label>
        <H1>Know your team.<br />Start with yourself.</H1>
        <p style={{ fontSize: 15, color: T.text2, lineHeight: 1.6, margin: 0 }}>
          This is your personal profile. Fill it in on your own – honestly. It helps to build better teams, and helps you understand how you work best.
        </p>
      </div>
      <PrimaryBtn onClick={() => { setMyCode(''); setScreen('step1'); }}>Create my profile</PrimaryBtn>
      <div style={{ borderTop: `1px solid ${T.border}`, paddingTop: 24 }}>
        <div style={{ fontSize: 13, color: T.text2, marginBottom: 10 }}>Already have a profile? Enter your code:</div>
        <div style={{ display: 'flex', gap: 8 }}>
          <input value={codeInput} onChange={e => { setCodeInput(e.target.value.toUpperCase()); setCodeError(''); }}
            onKeyDown={e => { if (e.key === 'Enter') lookupCode(); }}
            placeholder="e.g. A3F9K2" maxLength={8}
            style={{ flex: 1, padding: 14, fontSize: 16, fontFamily: T.mono, border: `1.5px solid ${T.border}`, borderRadius: 10, background: '#fff', outline: 'none', letterSpacing: 2, textTransform: 'uppercase', boxSizing: 'border-box', minHeight: 52 }} />
          <button onClick={lookupCode} disabled={loadingCode}
            style={{ padding: '14px 22px', fontSize: 15, fontWeight: 500, fontFamily: T.font, background: T.dark, color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', minHeight: 52 }}>
            {loadingCode ? '…' : 'Go'}
          </button>
        </div>
        {codeError && <div style={{ fontSize: 13, color: '#C0392B', marginTop: 8 }}>{codeError}</div>}
      </div>
      <div style={{ marginTop: 24 }}>
        <SecondaryBtn onClick={() => setScreen('profLogin')}>Professor view</SecondaryBtn>
      </div>
    </Wrap>
  );

  // ── PROF LOGIN
  if (screen === 'profLogin') return (
    <Wrap>
      <Label>Professor view</Label>
      <H2>Enter your code</H2>
      <Sub>Access the class dashboard.</Sub>
      <input value={profInput} onChange={e => setProfInput(e.target.value)} type="password"
        onKeyDown={e => { if (e.key === 'Enter') { if (profInput === PROF_CODE) { setProfError(''); setScreen('prof'); } else setProfError('Wrong code.'); }}}
        placeholder="Professor code" autoFocus
        style={{ width: '100%', padding: 14, fontSize: 16, fontFamily: T.font, border: `1.5px solid ${T.border}`, borderRadius: 10, background: '#fff', outline: 'none', boxSizing: 'border-box', minHeight: 52, marginBottom: 12 }} />
      {profError && <div style={{ fontSize: 13, color: '#C0392B', marginBottom: 12 }}>{profError}</div>}
      <PrimaryBtn onClick={() => { if (profInput === PROF_CODE) { setProfError(''); setScreen('prof'); } else setProfError('Wrong code.'); }}>Enter</PrimaryBtn>
      <SecondaryBtn onClick={() => setScreen('home')}>← Back</SecondaryBtn>
    </Wrap>
  );

  // ── STEP 1
  if (screen === 'step1') return (
    <Wrap>
      <StepLabel step={1} />
      <H2>Your name and avatar</H2>
      <Sub>Pick an emoji that feels like you.</Sub>
      <Card>
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 8 }}>Use your real first name</div>
        <input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" autoFocus
          style={{ width: '100%', padding: 14, fontSize: 16, fontFamily: T.font, border: `1.5px solid ${T.border}`, borderRadius: 10, background: '#fff', outline: 'none', boxSizing: 'border-box', minHeight: 52, marginBottom: 20 }} />
        <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 12 }}>Pick your emoji</div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {EMOJIS.map(e => (
            <button key={e} onClick={() => setEmoji(e)}
              style={{ fontSize: 26, background: emoji === e ? T.accent + '22' : 'transparent', border: emoji === e ? `2px solid ${T.accent}` : `1px solid ${T.border}`, borderRadius: 10, padding: '6px 8px', cursor: 'pointer' }}>{e}</button>
          ))}
        </div>
      </Card>
      <PrimaryBtn onClick={() => setScreen('step2')} disabled={!name.trim()}>Next →</PrimaryBtn>
      <SecondaryBtn onClick={() => setScreen('home')}>← Back</SecondaryBtn>
    </Wrap>
  );

  // ── STEP 2
  if (screen === 'step2') return (
    <Wrap>
      <StepLabel step={2} />
      <H2>Your natural role</H2>
      <Sub>Pick the one that fits best – not what you want to be, what you actually are.</Sub>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 20 }}>
        {ROLES.map(r => (
          <button key={r.id} onClick={() => setRole(r.id)}
            style={{ background: role === r.id ? T.accent : '#fff', color: role === r.id ? '#fff' : T.text, border: `1.5px solid ${role === r.id ? T.accent : T.border}`, borderRadius: 12, padding: '16px 18px', textAlign: 'left', cursor: 'pointer' }}>
            <div style={{ fontWeight: 600, fontSize: 15 }}>{r.label}</div>
            <div style={{ fontSize: 13, opacity: 0.75, marginTop: 3 }}>{r.desc}</div>
          </button>
        ))}
      </div>
      <PrimaryBtn onClick={() => setScreen('step3')} disabled={!role}>Next →</PrimaryBtn>
      <SecondaryBtn onClick={() => setScreen('step1')}>← Back</SecondaryBtn>
    </Wrap>
  );

  // ── STEP 3
  if (screen === 'step3') return (
    <Wrap>
      <StepLabel step={3} />
      <H2>How you work</H2>
      <Sub>Drag each slider to where you actually land – not where you think you should be.</Sub>
      <Card>
        {SLIDERS.map(s => <SliderRow key={s.id} slider={s} value={sliders[s.id]} onChange={v => setSliders(p => ({ ...p, [s.id]: v }))} />)}
      </Card>
      <PrimaryBtn onClick={() => setScreen('step4')}>Next →</PrimaryBtn>
      <SecondaryBtn onClick={() => setScreen('step2')}>← Back</SecondaryBtn>
    </Wrap>
  );

  // ── STEP 4
  if (screen === 'step4') return (
    <Wrap>
      <StepLabel step={4} />
      <H2>Four questions</H2>
      <Sub>Be honest. Short answers are fine.</Sub>
      {QUESTIONS.map(q => (
        <Card key={q.id}>
          <div style={{ fontSize: 14, fontWeight: 500, lineHeight: 1.55, marginBottom: 12 }}>{q.label}</div>
          <textarea value={answers[q.id]} onChange={e => setAnswers(p => ({ ...p, [q.id]: e.target.value }))}
            rows={3} placeholder="Your answer…"
            style={{ width: '100%', border: `1.5px solid ${T.border}`, borderRadius: 10, padding: '12px 14px', fontSize: 15, resize: 'vertical', outline: 'none', fontFamily: T.font, boxSizing: 'border-box' }} />
        </Card>
      ))}
      {saveError && <div style={{ fontSize: 13, color: '#C0392B', marginBottom: 12 }}>{saveError}</div>}
      <PrimaryBtn onClick={save} loading={saving} disabled={QUESTIONS.some(q => !answers[q.id].trim())}>Save my profile →</PrimaryBtn>
      <SecondaryBtn onClick={() => setScreen('step3')}>← Back</SecondaryBtn>
    </Wrap>
  );

  // ── DONE
  if (screen === 'done') return (
    <Wrap>
      <div style={{ textAlign: 'center', paddingTop: 20 }}>
        <div style={{ fontSize: 72, marginBottom: 20 }}>{emoji}</div>
        <H1>Profile saved, {name}!</H1>
        <p style={{ fontSize: 15, color: T.text2, lineHeight: 1.6, marginBottom: 32 }}>Your profile is in the class dashboard. Your professor will share the results.</p>
        <div style={{ background: '#fff', border: `1.5px solid ${T.border}`, borderRadius: 14, padding: '20px 24px', marginBottom: 32 }}>
          <div style={{ fontSize: 13, color: T.text2, marginBottom: 8 }}>Your profile code</div>
          <div style={{ fontSize: 28, fontFamily: T.mono, fontWeight: 600, letterSpacing: 4, color: T.accent }}>{myCode}</div>
          <div style={{ fontSize: 12, color: T.text2, marginTop: 8 }}>Save this code to edit your profile later.</div>
        </div>
        <PrimaryBtn onClick={resetAndHome}>← Back to start</PrimaryBtn>
      </div>
    </Wrap>
  );

  // ── PROFESSOR DASHBOARD
  if (screen === 'prof') {
    const roleCounts = ROLES.reduce((a, r) => ({ ...a, [r.id]: profiles.filter(p => p.role === r.id).length }), {});
    return (
      <div style={{ fontFamily: T.font, color: T.text, background: T.bg, minHeight: '100vh' }}>
        <style>{globalCss}</style>
        <div style={{ maxWidth: 700, margin: '0 auto', padding: '40px 20px' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 32 }}>
            <div>
              <Label>Professor view</Label>
              <h1 style={{ fontSize: 24, fontWeight: 600 }}>Class profiles ({profiles.length})</h1>
            </div>
            <button onClick={() => setScreen('home')} style={{ background: 'none', border: `1px solid ${T.border}`, borderRadius: 8, padding: '8px 16px', fontSize: 14, cursor: 'pointer', color: T.text, fontFamily: T.font }}>← Exit</button>
          </div>
          <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 14, padding: 20, marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontFamily: T.mono, textTransform: 'uppercase', letterSpacing: 1.5, color: T.text2, marginBottom: 16 }}>Role distribution</div>
            {ROLES.map(r => (
              <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
                <div style={{ fontSize: 13, width: 140, flexShrink: 0 }}>{r.label}</div>
                <div style={{ flex: 1, height: 8, background: T.border, borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: 8, background: T.accent, borderRadius: 4, width: profiles.length ? `${(roleCounts[r.id] / profiles.length) * 100}%` : '0%', transition: 'width 0.4s' }} />
                </div>
                <div style={{ fontSize: 13, color: T.text2, width: 20, textAlign: 'right' }}>{roleCounts[r.id]}</div>
              </div>
            ))}
          </div>
          <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 14, padding: 20, marginBottom: 20 }}>
            <div style={{ fontSize: 12, fontFamily: T.mono, textTransform: 'uppercase', letterSpacing: 1.5, color: T.text2, marginBottom: 6 }}>Class tendencies</div>
            <div style={{ fontSize: 12, color: T.text2, marginBottom: 16 }}>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, marginRight: 16 }}>
                <span style={{ width: 10, height: 10, borderRadius: '50%', background: 'rgba(44,40,37,0.25)', display: 'inline-block' }} /> Student
              </span>
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                <span style={{ width: 14, height: 14, borderRadius: '50%', background: T.accent, display: 'inline-block', border: '2px solid #fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} /> Class average
              </span>
            </div>
            {SLIDERS.map(s => {
              const avg = profiles.length ? profiles.reduce((a, p) => a + (p.sliders?.[s.id] || 5), 0) / profiles.length : 5;
              const dots = profiles.map(p => p.sliders?.[s.id] || 5);
              return (
                <div key={s.id} style={{ marginBottom: 20 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: T.text2, marginBottom: 8 }}>
                    <span>{s.left}</span><span style={{ fontWeight: 500, color: T.text }}>{s.label}</span><span>{s.right}</span>
                  </div>
                  <div style={{ height: 28, background: T.border, borderRadius: 4, position: 'relative' }}>
                    {/* student dots */}
                    {dots.map((v, i) => (
                      <div key={i} title={profiles[i]?.name} style={{ position: 'absolute', left: `${pct(v)}%`, top: '50%', transform: 'translate(-50%, -50%)', width: 10, height: 10, borderRadius: '50%', background: 'rgba(44,40,37,0.3)', border: '1.5px solid #fff', zIndex: 1 }} />
                    ))}
                    {/* class average */}
                    <div style={{ position: 'absolute', left: `${pct(avg)}%`, top: '50%', transform: 'translate(-50%, -50%)', width: 18, height: 18, background: T.accent, borderRadius: '50%', border: '2.5px solid #fff', boxShadow: '0 1px 4px rgba(0,0,0,0.25)', zIndex: 2 }} />
                  </div>
                </div>
              );
            })}
          </div>
          <div style={{ fontSize: 12, fontFamily: T.mono, textTransform: 'uppercase', letterSpacing: 1.5, color: T.text2, marginBottom: 12 }}>Individual profiles</div>
          {profiles.length === 0 && <div style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 14, padding: 20 }}><p style={{ color: T.text2, fontSize: 14 }}>No profiles yet.</p></div>}
          {profiles.map(p => (
            <div key={p.name} style={{ background: '#fff', border: `1px solid ${T.border}`, borderRadius: 14, padding: 20, marginBottom: 12 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, cursor: 'pointer' }} onClick={() => setExpanded(expanded === p.name ? null : p.name)}>
                <span style={{ fontSize: 36 }}>{p.emoji}</span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 600, fontSize: 17 }}>{p.name}</div>
                  <div style={{ fontSize: 13, color: T.text2, marginTop: 2 }}>{ROLES.find(r => r.id === p.role)?.label || '–'}</div>
                </div>
                {p.code && <div style={{ fontSize: 11, fontFamily: T.mono, color: T.text2, letterSpacing: 1 }}>{p.code}</div>}
                <span style={{ color: T.text2, fontSize: 16 }}>{expanded === p.name ? '▲' : '▼'}</span>
              </div>
              {expanded === p.name && (
                <div style={{ marginTop: 16, borderTop: `1px solid ${T.border}`, paddingTop: 16 }}>
                  {SLIDERS.map(s => (
                    <div key={s.id} style={{ marginBottom: 14 }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: T.text2, marginBottom: 6 }}>
                        <span>{s.left}</span><span style={{ fontWeight: 500, color: T.text }}>{s.label}</span><span>{s.right}</span>
                      </div>
                      <div style={{ height: 6, background: T.border, borderRadius: 3, position: 'relative' }}>
                        <div style={{ position: 'absolute', left: `${pct(p.sliders?.[s.id] || 5)}%`, top: -5, width: 16, height: 16, background: T.accent, borderRadius: '50%', transform: 'translateX(-50%)', border: '2px solid #fff' }} />
                      </div>
                    </div>
                  ))}
                  {QUESTIONS.map(q => (
                    <div key={q.id} style={{ marginTop: 14 }}>
                      <div style={{ fontSize: 12, fontWeight: 500, color: T.text2, marginBottom: 6 }}>{q.label}</div>
                      <div style={{ fontSize: 14, color: T.text, lineHeight: 1.55, background: T.bg, borderRadius: 10, padding: '12px 14px' }}>{p.answers?.[q.id] || '–'}</div>
                    </div>
                  ))}
                  <div style={{ marginTop: 16 }}>
                    <button onClick={() => deleteProfile(p.name)} disabled={deleting === p.name}
                      style={{ width: '100%', padding: '12px 20px', fontSize: 14, fontFamily: T.font, background: 'transparent', color: '#C0392B', border: '1.5px solid #C0392B', borderRadius: 10, cursor: 'pointer', opacity: deleting === p.name ? 0.5 : 1 }}>
                      {deleting === p.name ? 'Deleting…' : 'Delete profile'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
          <button onClick={loadProfiles} style={{ width: '100%', padding: '12px 20px', fontSize: 14, fontFamily: T.font, background: 'transparent', color: T.text2, border: `1px solid ${T.border}`, borderRadius: 10, cursor: 'pointer', marginTop: 4 }}>↺ Refresh</button>
        </div>
      </div>
    );
  }

  return null;
}
